import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'
import type { LedgerSafeEditSuggestionKind } from './ledgerSafeEditSuggestionTypes'
import {
  asLedgerSafeSuggestionString,
  asLedgerSafeSuggestionStringArray
} from './ledgerSafeEditSuggestionRows'

export type LedgerSafeRowMotionSuggestionKind = Extract<
  LedgerSafeEditSuggestionKind,
  'move_mapping' | 'shift_range' | 'insert_row_and_move' | 'mark_gap' | 'clear_gap'
>

export interface LedgerSafeBoundaryShiftSuggestionEventParams {
  sourceWitnessId: string
  leftRowId: string
  deltaWords: number
}

export interface LedgerSafeSplitSuggestionEventParams {
  sourceWitnessId: string
  rowId: string
  afterWordIndex: number
}

export interface LedgerSafeMergeSuggestionEventParams {
  sourceWitnessId: string
  rowIds: string[]
}

export interface LedgerSafeRowMotionSuggestionEventParams {
  sourceWitnessId: string
  kind: LedgerSafeRowMotionSuggestionKind
  rowIds: string[]
  params: Record<string, unknown>
}

const hasKnownRows = (rowsById: Map<string, AnchoredRow>, rowIds: string[]): boolean =>
  rowIds.every(rowId => rowsById.has(rowId))

export const getLedgerSafeSuggestionEventSourceWitnessId = (event: LedgerSafeEditorialEvent): string => {
  const params = event.operation?.params || {}
  return asLedgerSafeSuggestionString(params.witnessId || event.affectedWitnessIds?.[0])
}

export const normalizeLedgerSafeRowMotionDirection = (value: unknown): 'up' | 'down' | null => {
  const direction = asLedgerSafeSuggestionString(value)
  return direction === 'up' || direction === 'down' ? direction : null
}

export const normalizeLedgerSafeInsertionPlacement = (value: unknown): 'above' | 'below' | null => {
  const placement = asLedgerSafeSuggestionString(value)
  return placement === 'above' || placement === 'below' ? placement : null
}

export const parseLedgerSafeBoundaryShiftSuggestionEvent = (
  event: LedgerSafeEditorialEvent,
  rowsById: Map<string, AnchoredRow>
): LedgerSafeBoundaryShiftSuggestionEventParams | null => {
  const params = event.operation?.params || {}
  const leftRowId = asLedgerSafeSuggestionString(params.leftRowId)
  const deltaWords = Number(params.deltaWords)
  if (!leftRowId || !Number.isFinite(deltaWords) || deltaWords === 0 || !rowsById.has(leftRowId)) {
    return null
  }
  return {
    sourceWitnessId: getLedgerSafeSuggestionEventSourceWitnessId(event),
    leftRowId,
    deltaWords
  }
}

export const parseLedgerSafeSplitSuggestionEvent = (
  event: LedgerSafeEditorialEvent,
  rowsById: Map<string, AnchoredRow>
): LedgerSafeSplitSuggestionEventParams | null => {
  const params = event.operation?.params || {}
  const rowId = asLedgerSafeSuggestionString(params.rowId)
  const afterWordIndex = Number(params.afterWordIndex)
  if (!rowId || !Number.isFinite(afterWordIndex) || afterWordIndex < 1 || !rowsById.has(rowId)) {
    return null
  }
  return {
    sourceWitnessId: getLedgerSafeSuggestionEventSourceWitnessId(event),
    rowId,
    afterWordIndex
  }
}

export const parseLedgerSafeMergeSuggestionEvent = (
  event: LedgerSafeEditorialEvent,
  rowsById: Map<string, AnchoredRow>
): LedgerSafeMergeSuggestionEventParams | null => {
  const rowIds = asLedgerSafeSuggestionStringArray(event.operation?.params?.rowIds)
  if (rowIds.length < 2 || !hasKnownRows(rowsById, rowIds)) {
    return null
  }
  return {
    sourceWitnessId: getLedgerSafeSuggestionEventSourceWitnessId(event),
    rowIds
  }
}

export const parseLedgerSafeRowMotionSuggestionEvent = (
  event: LedgerSafeEditorialEvent,
  rowsById: Map<string, AnchoredRow>
): LedgerSafeRowMotionSuggestionEventParams | null => {
  const kind = event.operation?.kind
  const params = event.operation?.params || {}
  const sourceWitnessId = getLedgerSafeSuggestionEventSourceWitnessId(event)

  if (kind === 'move_mapping') {
    const rowId = asLedgerSafeSuggestionString(params.rowId)
    const direction = normalizeLedgerSafeRowMotionDirection(params.direction)
    if (!rowId || !direction || !rowsById.has(rowId)) {
      return null
    }
    return { sourceWitnessId, kind, rowIds: [rowId], params: { rowId, direction } }
  }

  if (kind === 'insert_row_and_move') {
    const rowId = asLedgerSafeSuggestionString(params.rowId)
    const placement = normalizeLedgerSafeInsertionPlacement(params.placement)
    if (!rowId || !placement || !rowsById.has(rowId)) {
      return null
    }
    return { sourceWitnessId, kind, rowIds: [rowId], params: { rowId, placement } }
  }

  if (kind === 'shift_range') {
    const rowIds = asLedgerSafeSuggestionStringArray(params.rowIds)
    const direction = normalizeLedgerSafeRowMotionDirection(params.direction)
    if (rowIds.length < 1 || !direction || !hasKnownRows(rowsById, rowIds)) {
      return null
    }
    return { sourceWitnessId, kind, rowIds, params: { rowIds, direction } }
  }

  if (kind === 'mark_gap' || kind === 'clear_gap') {
    const rowId = asLedgerSafeSuggestionString(params.rowId)
    if (!rowId || !rowsById.has(rowId)) {
      return null
    }
    return { sourceWitnessId, kind, rowIds: [rowId], params: { rowId } }
  }

  return null
}
